<template>
  <div class="pigai-edit">
    <!-- 顶部工具栏 -->
    <header class="toolbar">
      <div class="left">
        <el-button
          icon="el-icon-back"
          size="small"
          circle
          @click="goBack"
        />
        <span class="title">图片批改</span>
      </div>

      <div class="center">
        <!-- 工具组 -->
        <el-button-group>
          <el-button
            v-for="t in tools"
            :key="t.key"
            :type="tool === t.key ? 'primary' : 'default'"
            size="small"
            @click="tool = t.key"
          >
            <i :class="t.icon" />
            {{ t.label }}
          </el-button>
        </el-button-group>

        <!-- 属性 -->
        <div class="attrs">
          <label>颜色</label>
          <input
            v-model="color"
            type="color"
            class="color-input"
            @change="syncColor"
          >

          <label>粗细</label>
          <el-slider
            v-model="lineWidth"
            :min="1"
            :max="20"
            :show-tooltip="false"
            style="width: 120px"
          />
          <span class="val">{{ lineWidth }}</span>
        </div>

        <!-- 操作 -->
        <el-button-group>
          <el-button
            size="small"
            :disabled="!canUndo"
            @click="undo"
          >
            <i class="el-icon-refresh-left" /> 撤销
          </el-button>
          <el-button
            size="small"
            :disabled="!canRedo"
            @click="redo"
          >
            <i class="el-icon-refresh-right" /> 重做
          </el-button>
          <el-button
            size="small"
            type="success"
            :loading="saving"
            @click="saveCorrection"
          >
            <i class="el-icon-check" /> 保存批改
          </el-button>
        </el-button-group>
      </div>
    </header>

    <!-- 主体区 -->
    <main class="body">
      <!-- 左侧画布 -->
      <section class="canvas-area">
        <div
          ref="canvasWrap"
          class="canvas-wrap"
          @mousedown="onDown"
          @mousemove="onMove"
          @mouseup="onUp"
          @mouseleave="onUp"
          @touchstart.prevent="onTouchStart"
          @touchmove.prevent="onTouchMove"
          @touchend.prevent="onTouchEnd"
        >
          <!-- 底图画布 -->
          <canvas
            ref="baseCanvas"
            class="canvas base-canvas"
          />
          <!-- 标注画布 -->
          <canvas
            ref="drawCanvas"
            class="canvas draw-canvas"
          />
        </div>

        <!-- 底部状态栏 -->
        <footer class="status-bar">
          <span>工具：{{ currentToolLabel }}</span>
          <span>历史：{{ historyIdx + 1 }} / {{ history.length }}</span>
          <span>缩放：{{ Math.round(scale * 100) }}%</span>
        </footer>
      </section>

      <!-- 右侧批改面板 -->
      <aside class="side-panel">
        <h3>批改信息</h3>

        <!-- 批注输入 -->
        <div class="field">
          <label>批改意见</label>
          <el-input
            v-model="comment"
            type="textarea"
            :rows="6"
            placeholder="请输入批改意见"
          />
        </div>

        <!-- 批改状态 -->
        <div class="field">
          <label>批改状态</label>
          <el-radio-group v-model="status" size="small">
            <el-radio label="correct">✅ 正确</el-radio>
            <el-radio label="error">❌ 错误</el-radio>
            <el-radio label="improve">🔄 待改进</el-radio>
          </el-radio-group>
        </div>

        <!-- 保存按钮 -->
        <el-button
          type="primary"
          style="width: 100%"
          :loading="saving"
          @click="saveCorrection"
        >
          保存批改
        </el-button>

        <!-- 进度条 -->
        <el-progress
          v-if="uploadProgress > 0"
          :percentage="uploadProgress"
          :stroke-width="4"
          :show-text="false"
          style="margin-top: 8px"
        />

        <!-- 错误提示 -->
        <el-alert
          v-if="error"
          :title="error"
          type="error"
          :closable="true"
          @close="error = ''"
          style="margin-top: 12px"
        />
      </aside>
    </main>
  </div>
</template>

<script>
/**
 * PigaiEdit 图片批改画板
 * 功能：绘图工具、撤销重做、批注输入、状态标记、保存上传、返回提示
 */
export default {
  name: 'PigaiEdit',
  data () {
    return {
      // 工具配置
      tools: [
        { key: 'pen', label: '画笔', icon: 'el-icon-edit' },
        { key: 'eraser', label: '橡皮', icon: 'el-icon-delete' },
        { key: 'rect', label: '矩形', icon: 'el-icon-crop' }
      ],
      tool: 'pen', // 当前工具
      color: '#ff0000', // 画笔颜色
      lineWidth: 3, // 线宽
      scale: 1, // 缩放比例（预留）

      // 画布元素
      baseCanvas: null,
      baseCtx: null,
      drawCanvas: null,
      drawCtx: null,

      // 绘制状态
      drawing: false,
      startX: 0,
      startY: 0,
      lastX: 0,
      lastY: 0,

      // 历史栈（>=20 步）
      history: [],
      historyIdx: -1,
      maxHistory: 30,

      // 图片信息（包含原始尺寸）
      baseImageInfo: null, // { id, name, dataURL, width, height, ratio }

      // 批改信息
      comment: '', // 批注
      status: 'correct', // correct | error | improve

      // UI 状态
      saving: false,
      uploadProgress: 0,
      error: ''
      ,
      leavingAfterSave: false
    }
  },
  
  computed: {
    currentToolLabel () {
      const t = this.tools.find(i => i.key === this.tool)
      return t ? t.label : ''
    },
    canUndo () {
      return this.historyIdx > 0
    },
    canRedo () {
      return this.historyIdx < this.history.length - 1
    }
  },

  async mounted () {
    // 先加载底图，确保初始化画布时能正确绘制
    await this.loadBaseImage()
    this.initCanvas()
    // 自动恢复草稿（IndexedDB）
    await this.restoreDraft()
  },

  beforeRouteLeave (to, from, next) {
    if (this.leavingAfterSave) {
      next()
      return
    }
    // 未保存提示
    if (this.hasUnsavedChange()) {
      this.$confirm('批改内容尚未保存，确定离开？', '提示', {
        confirmButtonText: '离开',
        cancelButtonText: '取消',
        type: 'warning'
      })
        .then(() => next())
        .catch(() => next(false))
    } else {
      next()
    }
  },

  methods: {

    /* 把图片地址转成 dataURL */
    urlToDataURL (src) {
      return new Promise((resolve, reject) => {
        const img = new Image()
        img.crossOrigin = 'anonymous'
        img.onload = () => {
          const canvas = document.createElement('canvas')
          canvas.width = img.width
          canvas.height = img.height
          const ctx = canvas.getContext('2d')
          ctx.drawImage(img, 0, 0)
          // 使用 PNG，避免有损压缩
          resolve(canvas.toDataURL('image/png'))
        }
        img.onerror = reject
        img.src = src
      })
    },

    /* 读取 dataURL 的原始像素尺寸 */
    async getImageSize (dataURL) {
      return new Promise((resolve, reject) => {
        const img = new Image()
        img.onload = () => resolve({ width: img.width, height: img.height })
        img.onerror = reject
        img.src = dataURL
      })
    },

    /* 加载底图 */
    async loadBaseImage () {
      const id = this.$route.query.id
       console.log('id', id)
      const url = this.$route.query.url
      if (!id && !url) {
        this.error = '缺少图片参数'
        return
      }
      let dataURL = null
      let name = '远程图片'

      try {
        // 远程图片统一走代理，避免 CORS 污染画布
        const proxy = `/pigai/proxy-image?url=${encodeURIComponent(url)}`
        dataURL = await this.urlToDataURL(proxy)
      } catch (e) {
        this.error = '远程图片加载失败'
        return
      }
      if (!dataURL) {
        this.error = '图片加载失败'
        return
      }
      // 读取原始尺寸
      try {
        const { width, height } = await this.getImageSize(dataURL)
        const ratio = width && height ? width / height : 1
        this.baseImageInfo = { id, name, dataURL, width, height, ratio }
      } catch (e) {
        this.baseImageInfo = { id, name, dataURL, width: 0, height: 0, ratio: 1 }
      }
      console.log('加载底图成功', this.baseImageInfo)
    },

    /* 初始化画布 */
    initCanvas () {
      this.baseCanvas = this.$refs.baseCanvas
      this.baseCtx = this.baseCanvas.getContext('2d')
      this.drawCanvas = this.$refs.drawCanvas
      this.drawCtx = this.drawCanvas.getContext('2d')

      // 使用图片原始尺寸作为画布尺寸，确保输出不缩放、不留白边
      const iw = this.baseImageInfo && this.baseImageInfo.width
      const ih = this.baseImageInfo && this.baseImageInfo.height
      if (iw && ih) {
        this.baseCanvas.width = iw
        this.baseCanvas.height = ih
        this.drawCanvas.width = iw
        this.drawCanvas.height = ih
      }

      // 绘制底图
      this.drawBaseImage()
    },

    /* 将底图画到 baseCanvas */
    drawBaseImage () {
      if (!this.baseImageInfo || !this.baseImageInfo.dataURL) return
      const img = new Image()
      // 统一使用已转好的 dataURL，无需再跨域
      img.onload = () => {
        // 按 1:1 原始尺寸绘制，不做缩放与居中填充
        const cw = this.baseCanvas.width
        const ch = this.baseCanvas.height
        this.baseCtx.clearRect(0, 0, cw, ch)
        this.baseCtx.drawImage(img, 0, 0)
        // 首次历史快照
        if (this.history.length === 0) {
          this.pushHistory()
        }
      }
      img.onerror = () => (this.error = '底图绘制失败')
      img.src = this.baseImageInfo.dataURL
    },

    /* 鼠标/触摸事件统一处理 */
    onDown (e) {
      const pos = this.eventPos(e)
      this.startDraw(pos.x, pos.y)
    },
    onMove (e) {
      if (!this.drawing) return
      const pos = this.eventPos(e)
      this.moveDraw(pos.x, pos.y)
    },
    onUp () {
      this.endDraw()
    },
    onTouchStart (e) {
      const touch = e.touches[0]
      const pos = this.eventPos(touch)
      this.startDraw(pos.x, pos.y)
    },
    onTouchMove (e) {
      if (!this.drawing) return
      const touch = e.touches[0]
      const pos = this.eventPos(touch)
      this.moveDraw(pos.x, pos.y)
    },
    onTouchEnd () {
      this.endDraw()
    },

    /* 开始绘制 */
    startDraw (x, y) {
      this.drawing = true
      this.startX = x
      this.startY = y
      this.lastX = x
      this.lastY = y
      const ctx = this.drawCtx
      ctx.save()
      ctx.strokeStyle = this.tool === 'eraser' ? '#ffffff' : this.color
      ctx.lineWidth = this.tool === 'eraser' ? this.lineWidth * 3 : this.lineWidth
      ctx.lineCap = 'round'
      ctx.lineJoin = 'round'
      ctx.globalCompositeOperation = this.tool === 'eraser' ? 'destination-out' : 'source-over'
      ctx.beginPath()
      ctx.moveTo(x, y)
    },

    /* 移动绘制 */
    moveDraw (x, y) {
      if (this.tool === 'pen' || this.tool === 'eraser') {
        this.drawCtx.lineTo(x, y)
        this.drawCtx.stroke()
      } else if (this.tool === 'rect') {
        // 矩形预览：擦除并重绘
        this.redrawFromHistory()
        this.drawCtx.save()
        this.drawCtx.strokeStyle = this.color
        this.drawCtx.lineWidth = this.lineWidth
        const w = x - this.startX
        const h = y - this.startY
        this.drawCtx.strokeRect(this.startX, this.startY, w, h)
        this.drawCtx.restore()
      }
      this.lastX = x
      this.lastY = y
    },

    /* 结束绘制 */
    endDraw () {
      if (!this.drawing) return
      this.drawing = false
      this.drawCtx.restore()
      this.pushHistory()
    },

    /* 坐标转换：屏幕 -> 画布 */
    eventPos (e) {
      const rect = this.drawCanvas.getBoundingClientRect()
      return {
        x: e.clientX - rect.left,
        y: e.clientY - rect.top
      }
    },

    /* 历史栈管理 */
    pushHistory () {
      const dataURL = this.drawCanvas.toDataURL()
      // 丢弃当前指针之后的历史
      this.history = this.history.slice(0, this.historyIdx + 1)
      this.history.push(dataURL)
      this.historyIdx += 1
      // 超限则清理头部
      while (this.history.length > this.maxHistory) {
        this.history.shift()
        this.historyIdx -= 1
      }
      // 自动保存草稿
      this.autoSaveDraft()
    },

    /* 撤销 */
    undo () {
      if (!this.canUndo) return
      this.historyIdx -= 1
      this.redrawFromHistory()
    },

    /* 重做 */
    redo () {
      if (!this.canRedo) return
      this.historyIdx += 1
      this.redrawFromHistory()
    },

    /* 从历史恢复 drawCanvas */
    redrawFromHistory () {
      const dataURL = this.history[this.historyIdx]
      if (!dataURL) return
      const img = new Image()
      img.onload = () => {
        this.drawCtx.clearRect(0, 0, this.drawCanvas.width, this.drawCanvas.height)
        this.drawCtx.drawImage(img, 0, 0)
      }
      img.src = dataURL
    },

    /* 同步颜色 */
    syncColor () {
      // 颜色选择器已 v-model，无需额外处理
    },

    /* 保存批改 */
    async saveCorrection () {
      if (this.saving) return
      this.saving = true
      this.uploadProgress = 0
      try {
        // 1. 合成最终图片（底图 + 标注）
        const finalDataURL = await this.composeFinalImage()
        // 2. 上传
        const uploadedUrl = await this.uploadImage(finalDataURL)
        const id = this.$route.query.id || (this.baseImageInfo && this.baseImageInfo.id)
        if (!id) {
          throw new Error('缺少id，无法更新结果链接')
        }
        
        // 获取 correctionIndex 参数，判断是替换还是添加
        const correctionIndex = this.$route.query.correctionIndex
        let res
        
        if (correctionIndex !== undefined && correctionIndex !== null && correctionIndex !== '') {
          // 替换模式：替换指定索引的图片
          const { data } = await this.$http.post('admin/pigai/updateResultArrWithSinglePicUrl', {
            id,
            index: parseInt(correctionIndex),
            newUrl: uploadedUrl,
            originPicUrl : this.$route.query.url
          })
          res = data
        } else {
          // 添加模式：添加新图片到结果数组
          const { data } = await this.$http.post('admin/pigai/appendPicUrlToResultArr', {
            id,
            newUrl: uploadedUrl
          })
          res = data
        }

        if (!res || res.code !== 200) {
          throw new Error((res && (res.message || (res.meta && res.meta.msg))) || '更新结果链接失败')
        }
        window.dispatchEvent(new CustomEvent('pigai-correction-uploaded', {
          detail: {
            baseKey: this.baseImageInfo.id || this.baseImageInfo.dataURL,
            url: uploadedUrl
          }
        }))
        window.dispatchEvent(new Event('correction-saved'))
        await this.clearDraft()
        this.$message.success('批改已保存')
        this.leavingAfterSave = true
        this.goBack()
      } catch (e) {
        console.error(e)
        this.error = e.message || '保存失败'
      } finally {
        this.saving = false
        this.uploadProgress = 0
      }
    },

    /* 合成最终图片 */
    composeFinalImage () {
      return new Promise((resolve) => {
        const canvas = document.createElement('canvas')
        const ctx = canvas.getContext('2d')
        // 输出画布使用图片原始尺寸，确保不缩放不留白边
        const { width, height } = this.baseCanvas
        canvas.width = width
        canvas.height = height
        // 先画底图
        ctx.drawImage(this.baseCanvas, 0, 0)
        // 再叠标注
        ctx.drawImage(this.drawCanvas, 0, 0)
        // PNG 避免压缩导致清晰度下降
        resolve(canvas.toDataURL('image/png'))
      })
    },

    /* 上传图片（带进度） */
    uploadImage (dataURL) {
      return new Promise((resolve, reject) => {
        const [header, base64] = dataURL.split(',')
        const mime = header.match(/:(.*?);/)[1]
        const binary = atob(base64)
        const bytes = new Uint8Array(binary.length)
        for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i)
        const blob = new Blob([bytes], { type: mime })
        const fd = new FormData()
        fd.append('images', blob, 'pigai-correction.png')

        const xhr = new XMLHttpRequest()
        xhr.open('POST', 'https://jxpyq666.com/common/uploads')
        const token = window.sessionStorage.getItem('token') || ''
        xhr.setRequestHeader('Authorization', token)

        xhr.upload.onprogress = (e) => {
          if (e.lengthComputable) {
            this.uploadProgress = Math.round((e.loaded / e.total) * 100)
          }
        }
        xhr.onload = () => {
          if (xhr.status === 200) {
            try {
              const res = JSON.parse(xhr.responseText)
              if (res.code === 200) {
                const url = (res.result && (res.result.url || res.result.path)) || null
                if (url) resolve(url)
                else reject(new Error('上传返回空 URL'))
              } else {
                reject(new Error(res.message || '上传失败'))
              }
            } catch (e) {
              reject(new Error('解析上传结果失败'))
            }
          } else {
            reject(new Error('上传网络错误'))
          }
        }
        xhr.onerror = () => reject(new Error('上传网络错误'))
        xhr.send(fd)
      })
    },

    /* 草稿管理（IndexedDB） */
    async autoSaveDraft () {
      if (!this.baseImageInfo || !this.baseImageInfo.id) return
      const dataURL = this.drawCanvas.toDataURL()
      const draft = {
        id: this.baseImageInfo.id,
        dataURL,
        comment: this.comment,
        status: this.status,
        updatedAt: Date.now()
      }
      const { saveDraft } = await import('../utils/db.js')
      await saveDraft(draft).catch(() => {})
    },

    async restoreDraft () {
      if (!this.baseImageInfo || !this.baseImageInfo.id) return
      const { getDraft } = await import('../utils/db.js')
      const draft = await getDraft(this.baseImageInfo.id).catch(() => null) 
      if (draft && draft.dataURL) {
        // 恢复绘制层
        const img = new Image()
        img.onload = () => {
          this.drawCtx.clearRect(0, 0, this.drawCanvas.width, this.drawCanvas.height)
          this.drawCtx.drawImage(img, 0, 0)
          this.pushHistory() // 作为历史第一步
        }
        img.src = draft.dataURL
        // 恢复批注
        if (draft.comment) this.comment = draft.comment
        if (draft.status) this.status = draft.status
      }
    },

    async clearDraft () {
      if (!this.baseImageInfo || !this.baseImageInfo.id) return
      const { deleteDraftByBaseId } = await import('../utils/db.js')
      await deleteDraftByBaseId(this.baseImageInfo.id).catch(() => {})
    },

    /* 未保存判断 */
    hasUnsavedChange () {
      // 简单判断：有历史且非初始状态
      return this.history.length > 1 || this.comment.trim() !== ''
    },

    /* 返回 */
    goBack () {
      const pid = this.$route.query.pid || ''
      if (pid) {
        this.$router.push({ path: '/pigaiDetail', query: { id: pid } })
      } else {
        this.$router.push('/pigaiDetail')
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.pigai-edit {
  display: flex;
  flex-direction: column;
  height: 100vh;
  background: #f5f5f5;
}

/* 顶部工具栏 */
.toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 16px;
  background: #fff;
  border-bottom: 1px solid #e5e5e5;
  flex-wrap: wrap;
  gap: 12px;
}
.left {
  display: flex;
  align-items: center;
  gap: 12px;
}
.title {
  font-size: 16px;
  font-weight: 500;
}
.center {
  display: flex;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
}
.attrs {
  display: flex;
  align-items: center;
  gap: 6px;
  label {
    font-size: 12px;
    color: #666;
  }
  .val {
    font-size: 12px;
    color: #666;
    min-width: 20px;
    text-align: right;
  }
}
.color-input {
  width: 28px;
  height: 28px;
  border: 1px solid #d9d9d9;
  border-radius: 4px;
  cursor: pointer;
}

/* 主体区 */
.body {
  flex: 1;
  display: flex;
  overflow: hidden;
}
.canvas-area {
  flex: 1;
  display: flex;
  flex-direction: column;
  background: #fafafa;
}
.canvas-wrap {
  flex: 1;
  position: relative;
  overflow: auto;
  background: #fff;
  border: 1px solid #e5e5e5;
  margin: 12px;
  border-radius: 6px;
}
.canvas {
  position: absolute;
  left: 0;
  top: 0;
  display: block;
}
.base-canvas {
  z-index: 1;
}
.draw-canvas {
  z-index: 2;
  cursor: crosshair;
}
.status-bar {
  display: flex;
  align-items: center;
  gap: 24px;
  padding: 8px 16px;
  font-size: 12px;
  color: #666;
  background: #fff;
  border-top: 1px solid #e5e5e5;
  margin: 0 12px 12px 12px;
  border-radius: 0 0 6px 6px;
}

/* 右侧面板 */
.side-panel {
  width: 280px;
  background: #fff;
  border-left: 1px solid #e5e5e5;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  overflow-y: auto;
}
h3 {
  margin: 0 0 8px 0;
  font-size: 14px;
  font-weight: 500;
}
.field {
  display: flex;
  flex-direction: column;
  gap: 6px;
  label {
    font-size: 12px;
    color: #666;
  }
}

/* 响应式 */
@media (max-width: 768px) {
  .body {
    flex-direction: column;
  }
  .side-panel {
    width: 100%;
    border-left: none;
    border-top: 1px solid #e5e5e5;
  }
}
</style>